import { Suspense, lazy, useMemo, useState } from 'react';
import { PrivacyBanner } from './components/PrivacyBanner';
import { PocketLoader } from './components/PocketLoader';

/**
 * The full edition's loader — and with it the entire parser/streaming layer —
 * is referenced ONLY through this dynamic import. In the pocket build,
 * __POCKET__ folds to true, the import() below is dead code, and Rollup never
 * emits the chunk: the ingest path is absent from the artifact, not disabled.
 * (A static import survived tree-shaking via module side-effect caution; a
 * dead dynamic import is unambiguous.) The full edition gains code-splitting —
 * the ingest chunk loads on demand.
 */
const FileLoader = __POCKET__
  ? (null as unknown as ReturnType<typeof lazy>)
  : lazy(() =>
      import('./components/FileLoader').then((m) => ({ default: m.FileLoader })),
    );
import { Overview } from './components/Overview';
import { VariantTable } from './components/VariantTable';
import { Analysis } from './components/Analysis';
import { ConditionSearch } from './components/ConditionSearch';
import { BuildInfo } from './components/BuildInfo';
import { VaultTab } from './components/VaultTab';
import { Ideogram } from './components/Ideogram';
import { Nucleus3D } from './components/Nucleus3D';
import { VaultProvider, useVault } from './lib/vault';
import { computeStats } from './lib/stats';
import { useTheme, type Theme } from './lib/theme';
import type { ParsedGenome } from './types';

type Tab = 'overview' | 'browse' | 'conditions' | 'map' | 'nucleus' | 'analysis' | 'vault';

function Logo() {
  // Minimal double-helix mark.
  return (
    <svg className="logo" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M7 3c0 4 10 4 10 9s-10 5-10 9" stroke="var(--accent)" strokeWidth="1.6" strokeLinecap="round" />
      <path d="M17 3c0 4-10 4-10 9s10 5 10 9" stroke="var(--accent-2)" strokeWidth="1.6" strokeLinecap="round" />
      <path d="M8.4 6h7.2M8.4 9.6h7.2M8.4 14.4h7.2M8.4 18h7.2" stroke="var(--text-dim)" strokeWidth="1.2" strokeLinecap="round" />
    </svg>
  );
}

function ThemeToggle({ theme, onToggle }: { theme: Theme; onToggle: () => void }) {
  const next = theme === 'dark' ? 'light' : 'dark';
  return (
    <button
      className="ghost icon-btn"
      onClick={onToggle}
      title={`Switch to ${next} mode`}
      aria-label={`Switch to ${next} mode`}
    >
      {theme === 'dark' ? (
        // Sun — clicking moves you to light.
        <svg width="17" height="17" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <circle cx="12" cy="12" r="4.2" stroke="currentColor" strokeWidth="1.6" />
          <path
            d="M12 2.6v2.2M12 19.2v2.2M2.6 12h2.2M19.2 12h2.2M5.4 5.4l1.6 1.6M17 17l1.6 1.6M18.6 5.4L17 7M7 17l-1.6 1.6"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
          />
        </svg>
      ) : (
        // Moon — clicking moves you to dark.
        <svg width="17" height="17" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path
            d="M20 14.2A8.2 8.2 0 019.8 4a8.4 8.4 0 106.4 12.6c1.5 0 2.9-.9 3.8-2.4z"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinejoin="round"
          />
        </svg>
      )}
    </button>
  );
}

export function App() {
  return (
    <VaultProvider>
      <AppShell />
    </VaultProvider>
  );
}

function AppShell() {
  const [genome, setGenome] = useState<ParsedGenome | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<Tab>('overview');
  const { theme, toggle } = useTheme();
  const { notes } = useVault();
  const queuedCount = notes.length;

  const stats = useMemo(() => (genome ? computeStats(genome) : null), [genome]);

  return (
    <div className="app">
      <header className="masthead">
        <div className="brand">
          <Logo />
          <div>
            <h1>
              <span className="name">i-seng</span>
              <BuildInfo />
            </h1>
            <p className="tag">personal genome browser · {__POCKET__ ? 'pocket edition · nothing to upload' : 'private by design'}</p>
          </div>
        </div>
        <div className="masthead-actions">
          {genome && (
            <button
              className="ghost"
              onClick={() => {
                setGenome(null);
                setError(null);
                setTab('overview');
              }}
            >
              Clear data
            </button>
          )}
          <ThemeToggle theme={theme} onToggle={toggle} />
        </div>
      </header>

      <PrivacyBanner />

      {error && (
        <div className="note" style={{ marginBottom: 18 }} role="alert">
          {error}
        </div>
      )}

      {!genome &&
        (__POCKET__ ? (
          // Pocket edition: the ingest path is not rendered AND not bundled —
          // this ternary folds at build time, so FileLoader (and with it the
          // whole parser/streaming layer) tree-shakes out of the pocket build.
          <PocketLoader
            onLoaded={(g) => {
              setGenome(g);
              setError(null);
              setTab('overview');
            }}
          />
        ) : (
          <Suspense fallback={<div className="card" aria-busy="true" />}>
            <FileLoader
              onLoaded={(g: ParsedGenome) => {
                setGenome(g);
                setError(null);
                setTab('overview');
              }}
              onError={(m: string) => setError(m)}
            />
          </Suspense>
        ))}

      {genome && stats && (
        <>
          <nav className="tabs" aria-label="Views">
            <button className={tab === 'overview' ? 'active' : ''} onClick={() => setTab('overview')}>
              Overview
            </button>
            <button className={tab === 'browse' ? 'active' : ''} onClick={() => setTab('browse')}>
              Browse
            </button>
            <button className={tab === 'conditions' ? 'active' : ''} onClick={() => setTab('conditions')}>
              Conditions
            </button>
            <button className={tab === 'analysis' ? 'active' : ''} onClick={() => setTab('analysis')}>
              Analysis
            </button>
            <button className={tab === 'map' ? 'active' : ''} onClick={() => setTab('map')}>
              Map
            </button>
            <button className={tab === 'nucleus' ? 'active' : ''} onClick={() => setTab('nucleus')}>
              3D
            </button>
            <button className={tab === 'vault' ? 'active' : ''} onClick={() => setTab('vault')}>
              Vault{queuedCount > 0 && <span className="tab-count">{queuedCount}</span>}
            </button>
          </nav>

          {tab === 'overview' && <Overview genome={genome} stats={stats} theme={theme} />}
          {tab === 'browse' && <VariantTable genome={genome} />}
          {tab === 'conditions' && <ConditionSearch genome={genome} />}
          {tab === 'analysis' && <Analysis stats={stats} theme={theme} />}
          {tab === 'map' && <Ideogram genome={genome} />}
          {tab === 'nucleus' && <Nucleus3D genome={genome} />}
          {tab === 'vault' && <VaultTab />}
        </>
      )}

      <footer className="footer">
        {__POCKET__ ? (
          <>
            i-seng pocket edition — synthetic sample genomes only. It cannot read
            files, by design. Real data belongs in the full edition, run locally.
          </>
        ) : (
          <>
            i-seng runs entirely in your browser. Source stays on your machine ·
            data files are <code>.gitignore</code>d by default.
          </>
        )}
      </footer>
    </div>
  );
}
